import useFeature from '@/hooks/useFeature';
import { useAuth } from '@/context/auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import { networkService } from '@/services/network.service';
import { useEffect } from 'react';
import { safeToastInfo } from '@/components/ui/toastManager';

export interface PremiumFeatureOptions {
  /**
   * Show a toast notification if feature is not available
   */
  showToast?: boolean;
  
  /**
   * Custom message for toast/modal
   */
  message?: string;
  
  /**
   * Log access attempt for analytics
   */
  logAttempt?: boolean;
}

export const usePremiumFeatures = () => {
  const { isFeatureEnabled, loadingFeatures } = useFeature();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOnline = networkService.isOnline();
  
  // Store premium state in localStorage when online
  useEffect(() => {
    if (isOnline && user?.isPremium !== undefined) {
      localStorage.setItem('userIsPremium', user.isPremium ? 'true' : 'false');
      localStorage.setItem('premiumStatusLastUpdated', new Date().toISOString());
    }
  }, [isOnline, user?.isPremium]);
  
  /**
   * Check if a premium feature is enabled, with improved offline support
   */
  const isPremiumEnabled = (featureKey: string): boolean => {
    // If online, use the feature service
    if (isOnline) {
      const hasFeature = isFeatureEnabled(featureKey);

      // Fallback: if features are still loading but user is premium,
      // assume premium features are enabled
      if (loadingFeatures && user?.isPremium) {
        const fallbackPremium =
          featureKey.startsWith('premium_') ||
          ['unlimited_lists', 'list_backgrounds', 'product_photos', 'custom_themes', 'offline_mode'].includes(featureKey);
        if (fallbackPremium) {
          return true;
        }
      }

      return hasFeature;
    }
    
    // If offline, check multiple sources for premium status
    // 1. Check localStorage flag (set when user was online)
    const storedPremiumStatus = localStorage.getItem('userIsPremium') === 'true';
    
    // 2. Check user object premium flag
    const userIsPremium = user?.isPremium === true;
    
    // Use either source to determine premium status
    const isPremium = storedPremiumStatus || userIsPremium;
    
    // For offline mode, we need to explicitly check if user is premium
    if (featureKey === 'offline_mode' && isPremium) {
      return true;
    }
    
    // For other premium features, check if they're in the "allowed offline" list
    const offlinePremiumFeatures = [
      'offline_mode',
      'unlimited_lists',
      'premium_features',
      'list_backgrounds',
      'product_photos',
      'custom_themes',
      'list_store_binding'
    ];
    
    if (offlinePremiumFeatures.includes(featureKey) && isPremium) {
      return true;
    }
    
    // Default to false for features we can't verify offline
    return false;
  };
  
  /**
   * Try to use a premium feature - returns true if accessible, 
   * false if not and optionally shows notification
   */
  const tryUseFeature = (
    featureKey: string, 
    options: PremiumFeatureOptions = {}
  ): boolean => {
    const { 
      showToast = true, 
      message = "Questa funzionalità è disponibile solo con l'abbonamento Premium",
      logAttempt = true
    } = options;
    
    // Check if this is a premium feature
    const isPremiumFeature = featureKey.startsWith('premium_') ||
                             ['unlimited_lists', 'list_backgrounds', 'product_photos', 'custom_themes', 'offline_mode', 'list_store_binding'].includes(featureKey);
    
    // If not a premium feature, allow access
    if (!isPremiumFeature) {
      return true;
    }
    
    // For offline mode, use our offline-friendly check
    const isEnabled = isPremiumEnabled(featureKey);
    
    // Feature is enabled, allow access
    if (isEnabled) {
      return true;
    }
    
    // Log the attempt
    if (logAttempt) {
      logger.info(`Premium feature access attempt: ${featureKey} by user ${user?.id || 'unknown'}`);
    }
    
    // Show toast if requested
    if (showToast) {
      safeToastInfo(message, {
        description: "Clicca qui per scoprire Premium"
      });
      // Handle navigation separately
      setTimeout(() => {
        navigate('/premium');
      }, 1000);
    }
    
    // Return false to indicate feature is not accessible
    return false;
  };
  
  /**
   * Navigate to premium page with specific feature highlighted
   */
  const goToPremiumPage = (featureKey?: string) => {
    navigate(`/premium${featureKey ? `?feature=${featureKey}` : ''}`);
  };
  
  return {
    isPremiumEnabled,
    tryUseFeature,
    goToPremiumPage
  };
};

export default usePremiumFeatures;
